/**
 * Wallet telemetry trigger (Option B — server-side, no browser pixel).
 *
 * add2wallet is one frontend serving many merchants, so a publishable-key
 * pixel (one key ↔ one merchantId) can't attribute events to the real
 * per-pass merchant. Instead the page pings the add2wallet API, which resolves
 * merchant_id from wallet_shorturl_keywords and emits the event server-side
 * with the real merchant_id (HMAC server SDK).
 *
 * Query-only POST (event in the query string, no JSON body / custom headers)
 * so it's a CORS "simple request" with no preflight — same shape as the
 * existing /pass-followup call. `keepalive` lets it survive the navigation
 * that follows a button tap.
 */
export type WalletClientEvent =
  | "WALLET_LINK_VIEWED"
  | "WALLET_APPLE_BUTTON_CLICKED"
  | "WALLET_GOOGLE_BUTTON_CLICKED"
  | "WALLET_SAMSUNG_BUTTON_CLICKED"
  // 2026-08 landing-page redesign interactions. Whitelisted server-side in
  // walletHandler's WALLET_CLIENT_EVENTS — keep the two lists in sync.
  | "WALLET_PASS_PREVIEWED"
  | "WALLET_PASS_FLIPPED"
  | "WALLET_SECTION_EXPANDED"
  | "WALLET_HELP_CLICKED";

export function trackWalletEvent(
  apiBase: string,
  keyword: string | undefined,
  event: WalletClientEvent,
): void {
  if (!apiBase || !keyword) return;
  try {
    const isDevelopment =
      window.location.hostname === "localhost" ||
      window.location.hostname.startsWith("192.168.") ||
      window.location.hostname.startsWith("10.") ||
      window.location.hostname === "127.0.0.1";
    const path = `/wallet-event/${encodeURIComponent(keyword)}?e=${event}`;
    const url = isDevelopment ? `/api${path}` : `${apiBase}${path}`;
    fetch(url, { method: "POST", keepalive: true }).catch(() => {
      /* fire-and-forget */
    });
  } catch {
    /* never let telemetry break the UI */
  }
}
